import { createPassword, requestOtp, signInWithOtp } from '@/services/levo';
import { useAppDispatch } from '@/store';
import { logout } from '@/store/slices/userSlice';
import {
  Button,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import { useLevo } from '@levo-so/react';
import { useMutation } from '@tanstack/react-query';
import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { IoMdClose } from 'react-icons/io';
import * as yup from 'yup';
import FormInput from '../formComponents/FormInput';
import InputOtp from '../formComponents/InputOtp';
import { errorToast } from '../Toasts';

interface IForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface IFormValues {
  email: string;
}

const ForgotPasswordModal: React.FunctionComponent<
  IForgotPasswordModalProps
> = ({ isOpen, onClose }) => {
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [showResetPasswordInput, setShowResetPasswordInput] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const { membership } = useLevo();
  const dispatch = useAppDispatch();
  const { mutate, isLoading } = useMutation({
    mutationFn: requestOtp,
    onSuccess: (data) => {
      setShowOtpInput(true);
      setUserEmail(values.email);
    },
    onError: (error: any) => {
      errorToast(
        error?.json?.content?.message ===
          'Public signups are not allowed for this workspace. You are not an existing user. Please contact support to sign up.'
          ? 'You are not an existing user. Please contact support to sign up.'
          : 'Something went wrong'
      );
    },
  });

  const { mutate: verifyOtp, isLoading: isVerifyingOtp } = useMutation({
    mutationFn: signInWithOtp,
    onSuccess: () => {
      setShowOtpInput(false);
      setShowResetPasswordInput(true);
      setOtpError('');
    },
    onError: () => {
      errorToast('Invalid OTP. Please try again.');
      setOtp('');
    },
  });

  const { mutate: resetPassword, isLoading: isResettingPassword } = useMutation(
    {
      mutationFn: createPassword,
      onSuccess: () => {
        dispatch(logout());
        membership?.signOut().catch((e) => console.error(e));
        handleModalReset();
        onClose();
      },
      onError: () => {
        errorToast('Failed to reset password. Please try again.');
        setPasswordError('Failed to reset password. Please try again.');
        setNewPassword('');
      },
    }
  );

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    resetForm,
  } = useFormik<IFormValues>({
    initialValues: {
      email: '',
    },
    validationSchema: yup.object().shape({
      email: yup.string().email('Invalid email').required('Email is required'),
    }),
    onSubmit: (values) => {
      mutate({ data: { email: values.email } });
    },
  });

  const handleOtpChange = (otpValue: string) => {
    setOtp(otpValue);
    setOtpError('');
  };

  const handleOtpComplete = (otpValue: string) => {
    setOtp(otpValue);
  };

  const handleVerifyOtp = () => {
    if (otp.length < 4) {
      setOtpError('Please enter the complete OTP');
      return;
    }
    verifyOtp({ content: userEmail, code: otp });
    console.log(otp, userEmail);
  };

  const handleResetPassword = () => {
    if (!newPassword || newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }
    resetPassword({ password: newPassword });
  };

  const handleBackToEmail = () => {
    setShowOtpInput(false);
    setShowResetPasswordInput(false);
    setOtp('');
    setOtpError('');
    setNewPassword('');
    setPasswordError('');
  };

  const handleModalReset = () => {
    setShowOtpInput(false);
    setShowResetPasswordInput(false);
    setOtp('');
    setOtpError('');
    setUserEmail('');
    setNewPassword('');
    setPasswordError('');
    resetForm();
  };

  useEffect(() => {
    if (!isOpen) {
      handleModalReset();
    }
  }, [isOpen]);

  return (
    <Modal
      isCentered
      onClose={onClose}
      size="xl"
      isOpen={isOpen}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent maxW="500px" w="100%">
        <ModalHeader display="flex" justifyContent="space-between">
          <Heading
            variant="loginHeading"
            fontSize={{ xs: '1.3rem', sm: '1.6rem ', md: '1.9rem' }}
          >
            {showResetPasswordInput
              ? 'Reset Password'
              : showOtpInput
              ? 'Verify OTP'
              : 'Forgot Password'}
          </Heading>
          <Button
            variant="unstyled"
            right="2"
            top="2"
            aria-label="close Modal"
            bgColor="transparent"
            onClick={onClose}
            cursor="pointer"
          >
            <IoMdClose size="1.7rem" color="rgba(100, 100, 140, 1)" />
          </Button>
        </ModalHeader>
        <ModalBody pb={6}>
          {showResetPasswordInput ? (
            <>
              <Text mb={4} fontSize="sm" color="gray.600">
                Enter your new password for <b>{userEmail}</b>
              </Text>
              <FormControl isInvalid={!!passwordError} mb={4}>
                <FormLabel>Enter your new password</FormLabel>
                <FormInput
                  width="100%"
                  name="newPassword"
                  label="New Password"
                  placeholder="Enter your new password"
                  type="password"
                  value={newPassword}
                  onChange={(e: any) => {
                    setNewPassword(e.target.value);
                    setPasswordError('');
                  }}
                  error={passwordError}
                  touched={!!passwordError}
                />
              </FormControl>
              <Button
                w="100%"
                mb={3}
                bg="primary.500"
                color="white"
                isLoading={isResettingPassword}
                loadingText="Resetting..."
                _hover={{ bg: 'primary.600' }}
                onClick={handleResetPassword}
                type="submit"
                isDisabled={!newPassword || newPassword.length < 6}
              >
                Set New Password
              </Button>
              <HStack spacing={2} w="100%">
                <Button
                  flex={1}
                  variant="outline"
                  onClick={handleBackToEmail}
                  isDisabled={isResettingPassword || isLoading}
                >
                  Back
                </Button>
              </HStack>
            </>
          ) : showOtpInput ? (
            <>
              <Text mb={4} fontSize="sm" color="gray.600">
                We've sent a 4-digit code to {userEmail}
              </Text>

              <FormControl isInvalid={!!otpError} mb={4}>
                <FormLabel>Enter the OTP</FormLabel>
                <InputOtp
                  maxLength={4}
                  value={otp}
                  onChange={handleOtpChange}
                  onComplete={handleOtpComplete}
                  errorMessage={otpError}
                  isInvalid={!!otpError}
                  helperText="Enter the 4-digit OTP sent to your email"
                  placeholder="0"
                />
              </FormControl>

              <Button
                w="100%"
                mb={3}
                bg="primary.500"
                color="white"
                isLoading={isVerifyingOtp}
                loadingText="Verifying..."
                _hover={{
                  bg: 'primary.600',
                }}
                onClick={handleVerifyOtp}
                isDisabled={otp.length < 4}
              >
                Verify OTP
              </Button>

              <HStack spacing={2} w="100%">
                <Button
                  flex={1}
                  variant="outline"
                  onClick={handleBackToEmail}
                  isDisabled={isVerifyingOtp || isLoading}
                >
                  Back
                </Button>
              </HStack>
            </>
          ) : (
            <>
              <Text mb={4} fontSize="sm" color="gray.600">
                Enter your email address and we'll send you a code to reset your
                password.
              </Text>

              <FormInput
                width="100%"
                name="email"
                label="Email"
                placeholder="Enter your email"
                type="email"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.email}
                touched={touched.email}
              />

              <Button
                w="100%"
                mt={4}
                bg="primary.500"
                color="white"
                isLoading={isLoading}
                loadingText="Sending Code..."
                _hover={{
                  bg: 'primary.600',
                }}
                onClick={() => handleSubmit()}
                type="submit"
                isDisabled={!values.email || !!errors.email}
              >
                Send OTP
              </Button>
            </>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ForgotPasswordModal;
